class MixAndMatchBuilder extends HTMLElement {
  async connectedCallback() {
    this._errorEl = this.querySelector('[data-mix-error]');
    this._submitBtn = this.querySelector('[data-mix-submit]');
    this._summaryEl = this.querySelector('[data-mix-summary]');
    this._regularEl = this.querySelector('[data-mix-regular]');
    this._discountEl = this.querySelector('[data-mix-discount]');
    this._totalEl = this.querySelector('[data-mix-total]');
    this._helperEl = this.querySelector('[data-mix-helper]');
    this._cards = Array.from(this.querySelectorAll('[data-mix-pick]'));

    this._propLabelA = this.dataset.propLabelA || 'Selection A';
    this._propLabelB = this.dataset.propLabelB || 'Selection B';
    this._propLabelC = this.dataset.propLabelC || 'Selection C';
    this._bundleLabel = this.dataset.bundleLabel || 'Mix & match bundle';
    this._duoHandle = (this.dataset.duoBundleHandle || '').trim();
    this._trioHandle = (this.dataset.trioBundleHandle || '').trim();
    this._sectionsToBundle = ['variant-added'];
    this._hasTrio = Boolean(this._trioHandle);
    const rewardsRoot = this.querySelector('.carpet-pack-rewards');
    this._rewardApcVariantId = (
      this.dataset.rewardApcVariantId || rewardsRoot?.dataset.rewardApcVariantId || ''
    ).trim();
    this._rewardComboVariantId = (this.dataset.rewardComboVariantId || '').trim();
    this._rewardShippingCents = Number.parseInt(rewardsRoot?.dataset.rewardShippingCents || '0', 10) || 0;
    this._rewardApcRetailCents = Number.parseInt(rewardsRoot?.dataset.rewardApcRetailCents || '0', 10) || 0;
    this._rewardDeoRetailCents = Number.parseInt(rewardsRoot?.dataset.rewardDeoRetailCents || '0', 10) || 0;

    this._duoProduct = null;
    this._trioProduct = null;
    this._duoVariant = null;
    this._trioVariant = null;
    this._trioResolveVersion = 0;

    const trioMapScript = this.querySelector('[data-trio-option-value-map]');
    try {
      this._trioOptionValueMap = trioMapScript ? JSON.parse(trioMapScript.textContent) : null;
    } catch (e) {
      this._trioOptionValueMap = null;
    }
    this._slotValues = ['', '', ''];
    this._activeSlot = 0;
    this._wasSelectionLocked = false;
    this._slotHints = [
      'Choose your first scent',
      'Add another · unlock first gift',
      'Add another · unlock second gift',
    ];
    this._progressLabelEl = this.querySelector('[data-mix-progress-label]');
    this._progressFillEl = this.querySelector('[data-mix-progress-fill]');
    this._scentPrices = {};
    this._cards.forEach((card) => {
      const scent = (card.dataset.scent || '').trim();
      const price = Number(card.dataset.price || 0);
      if (scent && price && this._scentPrices[scent] == null) this._scentPrices[scent] = price;
    });

    if (!this._duoHandle || !this._cards.length) return;

    this._rewardTiles = {
      shipping: this.querySelector('[data-reward-tier="shipping"]'),
      apc: this.querySelector('[data-reward-tier="apc"]'),
      deo: this.querySelector('[data-reward-tier="deo"]'),
    };

    this._bindVariantCards();
    this._bindMobileDock();
    this._renderSlotUI();

    if (this._submitBtn) {
      this._submitBtn.addEventListener('click', () => this._addBundleToCart());
    }
    await this._loadDuoProduct();
    if (this._hasTrio && !this._trioOptionValueMap) await this._ensureTrioProductLoaded();
    this._resolveBundleVariants();
    this._syncSubmitButton();
  }

  _syncSubmitButton() {
    if (!this._submitBtn) return;

    const active = this._isTrioActive() ? this._trioVariant : this._duoVariant;

    if (!active) {
      this._submitBtn.setAttribute('disabled', 'disabled');
      return;
    }
    if (active.available) {
      this._submitBtn.removeAttribute('disabled');
      this._showError('');
    } else {
      this._submitBtn.setAttribute('disabled', 'disabled');
      this._showError('Selected combination is sold out.');
    }
  }

  _isScentSoldOut(scent) {
    const cards = this._cards.filter((card) => (card.dataset.scent || '').trim() === scent);
    return cards.length > 0 && cards.every((card) => card.dataset.soldOut === 'true');
  }

  _bindVariantCards() {
    this._cards.forEach((card) => {
      if (card.dataset.soldOut === 'true') return;

      const addBtn = card.querySelector('[data-mix-pick-label]');
      const minusBtn = card.querySelector('[data-mix-qty-minus]');
      const plusBtn = card.querySelector('[data-mix-qty-plus]');

      addBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._addScentToBundle((card.dataset.scent || '').trim());
      });
      minusBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._removeScentFromBundle((card.dataset.scent || '').trim());
      });
      plusBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._addScentToBundle((card.dataset.scent || '').trim());
      });

      card.addEventListener('click', (e) => {
        if (e.target.closest('[data-mix-pick-label], [data-mix-qty]')) return;
        const scent = (card.dataset.scent || '').trim();
        if (!scent || this._scentCountInBundle(scent) > 0) return;
        this._addScentToBundle(scent);
      });
    });
  }

  _scentCountInBundle(scent) {
    return this._slotValues
      .slice(0, this._slotCount())
      .filter((val) => val === scent).length;
  }

  _addScentToBundle(scent) {
    if (!scent || this._isSelectionLocked() || this._isScentSoldOut(scent)) return;
    const slot = this._getNextEmptySlot();
    if (slot < 0) return;
    this._slotValues[slot] = scent;
    this._syncActiveSlot();
    this._resolveBundleVariants();
    this._renderSlotUI();
    this._syncSubmitButton();
  }

  _getNextEmptySlot() {
    const slotCount = this._slotCount();
    return this._slotValues.slice(0, slotCount).findIndex((value) => !value);
  }

  _syncActiveSlot() {
    const nextEmpty = this._getNextEmptySlot();
    const slotCount = this._slotCount();
    this._activeSlot = nextEmpty >= 0 ? nextEmpty : Math.max(0, slotCount - 1);
  }

  _compactSlots() {
    const slotCount = this._slotCount();
    const filled = this._slotValues.slice(0, slotCount).filter(Boolean);
    for (let i = 0; i < slotCount; i += 1) {
      this._slotValues[i] = filled[i] || '';
    }
  }

  _clearSlot(index) {
    this._slotValues[index] = '';
    this._compactSlots();
    this._syncActiveSlot();
    this._resolveBundleVariants();
    this._renderSlotUI();
    this._syncSubmitButton();
  }

  _removeScentFromBundle(scent) {
    const slotCount = this._slotCount();
    const removeIndex = this._slotValues.slice(0, slotCount).findIndex((val) => val === scent);
    if (removeIndex >= 0) this._clearSlot(removeIndex);
  }

  _bindMobileDock() {
    this._sidebar = this.querySelector('[data-mix-sidebar]');
    this._dockToggle = this.querySelector('[data-mix-dock-toggle]');
    this._dockToggle?.addEventListener('click', () => this._toggleMobileDock());

    this._onDocClickDock = (e) => {
      if (!this._sidebar?.classList.contains('is-dock-open')) return;
      if (e.target.closest('[data-mix-sidebar]')) return;
      this._toggleMobileDock(false);
    };
    document.addEventListener('click', this._onDocClickDock, true);
  }

  disconnectedCallback() {
    if (this._onDocClickDock) {
      document.removeEventListener('click', this._onDocClickDock, true);
    }
  }

  _isMobileDock() {
    return window.matchMedia('(max-width: 743px)').matches;
  }

  _toggleMobileDock(open) {
    if (!this._sidebar || !this._isMobileDock()) return;
    const nextOpen = open ?? !this._sidebar.classList.contains('is-dock-open');
    this._sidebar.classList.toggle('is-dock-open', nextOpen);
    this._dockToggle?.setAttribute('aria-expanded', nextOpen ? 'true' : 'false');
  }

  _syncMobileDockState() {
    if (!this._isMobileDock()) return;
    const locked = this._isSelectionLocked();
    if (locked && !this._wasSelectionLocked) {
      this._toggleMobileDock(true);
    } else if (!locked && this._wasSelectionLocked) {
      this._toggleMobileDock(false);
    }
    this._wasSelectionLocked = locked;
  }

  _syncMobileDockLabel() {
    const label = this.querySelector('[data-mix-dock-label]');
    const hint = this.querySelector('[data-mix-dock-hint]');
    const count = this._selectedScentCount();
    const total = this._slotCount();

    if (label) label.textContent = `Your bundle (${count}/${total})`;
    if (hint) {
      const nextIndex = this._slotValues.slice(0, total).findIndex((value) => !value);
      hint.textContent = nextIndex >= 0
        ? this._getSlotHint(nextIndex)
        : 'Ready — add to cart';
    }
  }

  _renderSlotUI() {
    const slotCount = this._slotCount();
    const labels = [this._propLabelA, this._propLabelB, this._propLabelC];
    const isLocked = this._isSelectionLocked();

    this._cards.forEach((card) => {
      const scent = (card.dataset.scent || '').trim();
      if (card.dataset.soldOut === 'true') {
        card.classList.add('is-sold-out');
        card.classList.remove('is-in-bundle', 'is-disabled');
        card.setAttribute('aria-label', `${scent} is out of stock`);
        return;
      }

      const countInBundle = this._scentCountInBundle(scent);
      const inBundle = countInBundle > 0;
      const addBtn = card.querySelector('[data-mix-pick-label]');
      const qty = card.querySelector('[data-mix-qty]');
      const qtyValue = card.querySelector('[data-mix-qty-value]');
      const plusBtn = card.querySelector('[data-mix-qty-plus]');

      card.classList.toggle('is-in-bundle', inBundle);
      card.classList.toggle('is-disabled', isLocked && !inBundle);

      if (addBtn) addBtn.hidden = inBundle;
      if (qty) qty.hidden = !inBundle;
      if (qtyValue) qtyValue.textContent = String(countInBundle);
      if (plusBtn) plusBtn.disabled = isLocked;

      card.setAttribute(
        'aria-label',
        inBundle
          ? `${scent} in bundle, quantity ${countInBundle}`
          : `Add ${scent} to bundle`,
      );
    });

    this._renderProgress();
    this._renderSelectionSummary(labels, slotCount);
    this._syncRewardTiers();
    this._renderPricing();
    this._syncMobileDockLabel();
    this._syncMobileDockState();
  }

  _renderProgress() {
    const count = this._selectedScentCount();
    const total = this._slotCount();
    if (this._progressLabelEl) {
      this._progressLabelEl.textContent = `${count} of ${total} scents selected`;
    }
    if (this._progressFillEl) {
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      this._progressFillEl.style.width = `${pct}%`;
    }
  }

  _selectedScentCount() {
    return this._slotValues.slice(0, this._slotCount()).filter(Boolean).length;
  }

  _syncRewardTiers() {
    const count = this._selectedScentCount();
    const unlockAt = { shipping: 1, apc: 2, deo: 3 };
    Object.entries(unlockAt).forEach(([tier, need]) => {
      const el = this._rewardTiles?.[tier];
      if (!el) return;
      if (tier === 'deo' && !this._hasTrio) {
        el.classList.remove('is-unlocked');
        return;
      }
      el.classList.toggle('is-unlocked', count >= need);
    });
  }

  _resolveBundleVariants() {
    this._duoVariant = null;
    this._trioVariant = null;

    if (this._duoProduct) {
      const [s1, s2] = this._slotValues;
      if (s1 && s2) {
        this._duoVariant = this._duoProduct.variants.find((v) => {
          const [vo1, vo2] = v.options || [];
          return vo1 === s1 && vo2 === s2;
        }) || this._findVariantIgnoringOrder(this._duoProduct.variants, [s1, s2]) || null;
      }
    }

    if (this._hasTrio) {
      const [s1, s2, s3] = this._slotValues;
      if (s1 && s2 && s3) {
        if (this._trioOptionValueMap) {
          this._resolveTrioVariantViaApi(s1, s2, s3);
        } else if (this._trioProduct) {
          this._trioVariant = this._trioProduct.variants.find((v) => {
            const [vo1, vo2, vo3] = v.options || [];
            return vo1 === s1 && vo2 === s2 && vo3 === s3;
          }) || this._findVariantIgnoringOrder(this._trioProduct.variants, [s1, s2, s3]) || null;
        }
      }
    }
  }

  async _resolveTrioVariantViaApi(a, b, c) {
    const version = ++this._trioResolveVersion;

    const id1 = this._trioOptionValueMap?.option1?.[a];
    const id2 = this._trioOptionValueMap?.option2?.[b];
    const id3 = this._trioOptionValueMap?.option3?.[c];

    if (id1 && id2 && id3) {
      const root = window.Shopify?.routes?.root || '/';
      try {
        const res = await fetch(
          `${root}products/${this._trioHandle}?section_id=variant-resolver&option_values=${id1},${id2},${id3}`,
        );
        if (version !== this._trioResolveVersion) return;
        if (res.ok) {
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const jsonText = doc.querySelector('[data-variant-resolver]')?.textContent;
          this._trioVariant = jsonText ? JSON.parse(jsonText) : null;
        }
      } catch (e) {
        if (version !== this._trioResolveVersion) return;
        console.error('[mix-and-match] trio resolve error', e);
      }
    } else {
      this._trioVariant = null;
    }

    if (version !== this._trioResolveVersion) return;
    this._syncSubmitButton();
    this._renderPricing();
  }

  _normalizeScent(value) {
    return String(value || '').trim().toLowerCase();
  }

  _buildCountMap(values) {
    const map = new Map();
    values.forEach((value) => {
      const key = this._normalizeScent(value);
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    return map;
  }

  _countMapsEqual(a, b) {
    if (a.size !== b.size) return false;
    for (const [key, value] of a.entries()) {
      if (b.get(key) !== value) return false;
    }
    return true;
  }

  _findVariantIgnoringOrder(variants, selectedValues) {
    if (!Array.isArray(variants) || !Array.isArray(selectedValues)) return null;
    const targetMap = this._buildCountMap(selectedValues);
    return variants.find((variant) => {
      const options = (variant.options || []).slice(0, selectedValues.length);
      if (options.length !== selectedValues.length) return false;
      const optionMap = this._buildCountMap(options);
      return this._countMapsEqual(targetMap, optionMap);
    }) || null;
  }

  _renderSelectionSummary(labels, slotCount) {
    if (!this._summaryEl) return;
    this._summaryEl.innerHTML = '';

    for (let i = 0; i < slotCount; i += 1) {
      const value = this._slotValues[i];

      const row = document.createElement('div');
      row.className = 'mix-summary-row';
      row.classList.toggle('mix-summary-row--filled', Boolean(value));
      row.classList.toggle('mix-summary-row--milestone', !value);

      const left = document.createElement('div');
      left.className = 'mix-summary-row__label';

      const slotLabel = document.createElement('span');
      slotLabel.className = 'mix-summary-row__slot';
      slotLabel.textContent = String(i + 1);
      if (value) {
        slotLabel.style.backgroundColor = this._getScentColor(value);
        slotLabel.style.color = '#ffffff';
      }

      const meta = document.createElement('div');
      meta.className = 'mix-summary-row__meta';

      const valueText = document.createElement('span');
      valueText.className = 'mix-summary-row__value';
      valueText.textContent = value || this._getSlotHint(i) || 'Not selected';
      meta.appendChild(valueText);

      if (value) {
        const priceText = document.createElement('span');
        priceText.className = 'mix-summary-row__price';
        priceText.textContent = `Original ${this._formatMoney(this._scentPrices[value] || 0)}`;
        meta.appendChild(priceText);
      }

      left.appendChild(slotLabel);
      left.appendChild(meta);
      row.appendChild(left);

      if (value) {
        const clearBtn = document.createElement('button');
        clearBtn.type = 'button';
        clearBtn.className = 'mix-summary-row__clear';
        clearBtn.setAttribute('aria-label', `Remove ${labels[i]}`);
        clearBtn.innerHTML = '&times;';
        clearBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this._clearSlot(i);
        });
        row.appendChild(clearBtn);
      }

      this._summaryEl.appendChild(row);
    }
  }

  _giftRetailCentsForCount(count) {
    let cents = 0;
    if (count >= 1) cents += this._rewardShippingCents;
    if (count >= 2) cents += this._rewardApcRetailCents;
    if (count >= 3 && this._hasTrio) cents += this._rewardDeoRetailCents;
    return cents;
  }

  _renderPricing() {
    const trioActive = this._isTrioActive();
    const activeVariant = trioActive ? this._trioVariant : this._duoVariant;
    const selectedValues = this._slotValues.slice(0, this._slotCount()).filter(Boolean);
    const regularFromSelections = selectedValues.reduce((sum, scent) => sum + (this._scentPrices[scent] || 0), 0);
    const giftRetail = this._giftRetailCentsForCount(selectedValues.length);
    let regular = activeVariant?.compare_at_price || regularFromSelections || activeVariant?.price || 0;
    if (giftRetail > 0) regular += giftRetail;
    const total = activeVariant?.price || 0;
    const discount = Math.max(0, regular - total);

    if (this._regularEl) this._regularEl.textContent = this._formatMoney(regular);
    if (this._discountEl) this._discountEl.textContent = `-${this._formatMoney(discount)}`;
    if (this._totalEl) this._totalEl.textContent = this._formatMoney(total);

    if (this._helperEl) {
      const nextIndex = this._slotValues.slice(0, this._slotCount()).findIndex((value) => !value);
      this._helperEl.textContent = nextIndex >= 0
        ? this._getSlotHint(nextIndex)
        : 'Ready — add to cart';
    }
  }

  async _loadDuoProduct() {
    try {
      const res = await fetch(
        `${window.Shopify.routes.root}products/${this._duoHandle}.js`,
      );
      if (!res.ok) throw new Error('fetch failed');
      this._duoProduct = await res.json();
    } catch (e) {
      console.error('[mix-and-match duo]', e);
      this._showError('Could not load bundle data.');
    }
  }

  /* ── Add to cart ──────────────────────────────────────────────────────── */

  async _addBundleToCart() {
    this._showError('');

    const trioActive = this._isTrioActive();
    const variant = trioActive ? this._trioVariant : this._duoVariant;

    const selectedCount = this._slotValues
      .slice(0, this._slotCount())
      .filter(Boolean).length;

    const requiredCount = trioActive ? 3 : 2;
    if (!variant || selectedCount < requiredCount) {
      this._showError('Please make your selections first.');
      return;
    }
    if (!variant.available) {
      this._showError('Selected combination is sold out.');
      return;
    }

    /* Line item properties from variant options */
    const propLabels = [this._propLabelA, this._propLabelB, this._propLabelC];
    const properties = { _bundle_name: this._bundleLabel };
    (variant.options || []).forEach((val, i) => {
      if (val != null && propLabels[i]) properties[propLabels[i]] = String(val);
    });

    this._submitBtn.setAttribute('disabled', 'disabled');
    this._submitBtn.setAttribute('aria-busy', 'true');

    const gifts = window.CarpetPackGifts;
    if (!gifts?.addBundleWithGifts) {
      this._showError('Could not add to cart.');
      this._submitBtn.removeAttribute('aria-busy');
      if (trioActive ? this._trioVariant?.available : this._duoVariant?.available) {
        this._submitBtn.removeAttribute('disabled');
      }
      console.error('[mix-and-match] CarpetPackGifts module not loaded');
      return;
    }

    try {
      await gifts.addBundleWithGifts({
        bundleItem: { quantity: 1, id: variant.id, properties },
        apcVariantId: this._rewardApcVariantId,
        comboVariantId: this._rewardComboVariantId,
        includeApc: !trioActive,
        includeCombo: trioActive,
        quantity: 1,
        sectionsToBundle: this._sectionsToBundle,
        sectionsUrl: `${window.Shopify.routes.root}variants/${variant.id}`,
      });

      if (
        window.themeVariables?.settings?.cartType === 'page' ||
        window.themeVariables?.settings?.pageType === 'cart'
      ) {
        window.location.href = `${window.Shopify.routes.root}cart`;
      }
    } catch (e) {
      console.error('[mix-and-match]', e);
      this._showError(e.message || 'Something went wrong. Please try again.');
    } finally {
      this._submitBtn.removeAttribute('aria-busy');
      if (trioActive ? this._trioVariant?.available : this._duoVariant?.available) {
        this._submitBtn.removeAttribute('disabled');
      }
    }
  }

  /* ── Helpers ──────────────────────────────────────────────────────────── */

  _showError(msg) {
    if (!this._errorEl) return;
    this._errorEl.textContent = msg;
    this._errorEl.hidden = !msg;
  }

  async _ensureTrioProductLoaded() {
    if (this._trioProduct || !this._trioHandle) return;
    try {
      const res = await fetch(
        `${window.Shopify.routes.root}products/${this._trioHandle}.js`,
      );
      if (!res.ok) throw new Error('fetch failed');
      this._trioProduct = await res.json();
    } catch (e) {
      console.error('[mix-and-match trio]', e);
      this._showError('Could not load trio product.');
    }
  }

  _getSlotHint(slotIndex) {
    return this._slotHints[slotIndex] || 'Not selected';
  }

  _slotCount() {
    return this._hasTrio ? 3 : 2;
  }

  _isTrioActive() {
    return this._hasTrio && Boolean(this._slotValues[2]);
  }

  _isSelectionLocked() {
    return this._slotValues
      .slice(0, this._slotCount())
      .filter(Boolean).length >= this._slotCount();
  }

  _formatMoney(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  _getScentColor(scentValue) {
    const scent = String(scentValue || '').toLowerCase();
    if (scent.includes('lemon')) return '#FACC15';
    if (scent.includes('orange') || scent.includes('citrus')) return '#FB923C';
    if (scent.includes('lavender')) return '#A78BFA';
    if (scent.includes('pink')) return '#F472B6';
    if (scent.includes('mahogany') || scent.includes('coconut')) return '#A16207';
    if (scent.includes('fireside') || scent.includes('spice')) return '#B45309';
    if (scent.includes('volcano')) return '#459D69';
    if (scent.includes('cherry blossom')) return '#ECB8C3';
    if (scent.includes('cocoa cashmere') || scent.includes('cocoa butter')) return '#704831';
    if (scent.includes('mandarin')) return '#E4821D';
    if (scent.includes('lime sorbet') || scent.includes('lime')) return '#85A75A';
    if (scent.includes('champagne')) return '#A99D85';
    return '#036338';
  }
}

if (!window.customElements.get('mix-and-match-builder')) {
  window.customElements.define('mix-and-match-builder', MixAndMatchBuilder);
}
